import { ClockIcon, MapPinIcon, UsersIcon } from "@heroicons/react/24/outline";
import { BoltIcon } from "@heroicons/react/24/solid";
import cx from "classnames";
import { formatDistanceToNowStrict } from "date-fns";
import React, { type SyntheticEvent } from "react";
import reactUseCookie from "react-use-cookie";
import type { SERPConfig } from "../../../plugins/gatsby-source-serp/types";
import { ModalState } from "../../../plugins/gatsby-source-serp/types";
import JobLink from "../../components/JobLink";
import { trackCustomEvent } from "../../utils/analytics";

type Props = {
  job: any;
  tx: any;
  cpaModalTx: Queries.CpaModalFragment;
  hostedApplyTx: Queries.HostedApplyFragment;
  onSERPJobClick: SERPConfig["onSERPJobClick"];
  toggleModalState: (T: ModalState) => void;
  jbeModal: SERPConfig["jbeModal"];
  setRequestedJob: (T: string) => void;
  onClick?: (event: SyntheticEvent<Element, Event>) => void;
};

type PropsHTML = Pick<
  Props,
  "job" | "tx" | "cpaModalTx" | "hostedApplyTx" | "onClick"
>;

export function SerpJobLinkHTML({
  job,
  tx,
  cpaModalTx,
  hostedApplyTx,
  onClick,
}: PropsHTML) {
  const { city, company, title, summary, date, isTopJob } = job;
  return (
    <JobLink
      gaCategory="SERP"
      cpaModalTx={cpaModalTx}
      hostedApplyTx={hostedApplyTx}
      job={job}
      className={cx("group block bg-white p-4 hover:bg-gray-50", {
        "rounded-md shadow-[0_0_3px_#aaa]": !isTopJob,
        "border-left rounded-lg border-accent-500 shadow-[0_0_6px_#999,3px_0_0_#D40063_inset]":
          isTopJob,
      })}
      onClick={onClick}
    >
      <div
        className={cx({
          "sm:flex sm:items-start sm:justify-between sm:gap-2": !isTopJob,
          "flex flex-col": isTopJob,
        })}
      >
        <div className="sm:flex sm:shrink sm:grow-0 sm:flex-col">
          <h3
            className={cx("font-medium text-primary-600", {
              "flex items-baseline text-lg": isTopJob,
              "text-md ": !isTopJob,
            })}
          >
            {isTopJob ? (
              <BoltIcon className="mr-2 w-4 text-accent-500" />
            ) : null}
            {title}
          </h3>
          {company && (
            <p
              className={cx("flex items-center text-sm text-gray-500 sm:mt-4", {
                "mt-4": isTopJob,
                "mt-2": !isTopJob,
              })}
            >
              <UsersIcon
                className="mr-1.5 h-5 w-5 flex-shrink-0 "
                aria-hidden="true"
              />
              {company}
            </p>
          )}
          {city && city !== "null" && (
            <p
              className={cx("flex items-center text-sm text-gray-500 sm:mt-4", {
                "mt-4": isTopJob,
                "mt-2": !isTopJob,
              })}
            >
              <MapPinIcon
                className="mr-1.5 h-5 w-5 flex-shrink-0"
                aria-hidden="true"
              />
              {city}
            </p>
          )}
          {date && (
            <p
              className={cx("flex items-center text-sm text-gray-500 sm:mt-4", {
                "mt-4": isTopJob,
                "mt-2": !isTopJob,
              })}
            >
              <ClockIcon className="mr-1.5 h-5 w-5" />
              {tx!.postedXago!.replace(
                "{{time}}",
                formatDistanceToNowStrict(new Date(date))
              )}
            </p>
          )}
          <p
            className={cx(
              "multiline-truncate block overflow-hidden text-sm text-gray-500 ",
              {
                "mt-6": isTopJob,
                "mt-4": !isTopJob,
              }
            )}
          >
            {summary}
          </p>
        </div>
        <button
          className={cx(
            "flex w-full flex-shrink-0 items-center justify-center rounded-md border border-transparent  px-5 py-3 text-base font-medium text-white ",
            {
              "mt-8 bg-primary-600 hover:bg-primary-700": isTopJob,
              "mt-4 bg-primary-600 hover:bg-primary-700 sm:mt-0 sm:w-auto":
                !isTopJob,
            }
          )}
          tabIndex={-1}
        >
          {tx!.viewJob}
        </button>
      </div>
    </JobLink>
  );
}

export default function SerpJobLink({
  job,
  tx,
  cpaModalTx,
  hostedApplyTx,
  onSERPJobClick,
  toggleModalState,
  setRequestedJob,
  onClick,
}: Props) {
  const [hasRegisteredWithEmail] = reactUseCookie("email", "");
  return (
    <SerpJobLinkHTML
      job={job}
      tx={tx}
      cpaModalTx={cpaModalTx}
      hostedApplyTx={hostedApplyTx}
      onClick={(event) => {
        if (typeof onClick === "function") {
          onClick(event);
          return;
        }
        if (onSERPJobClick === "jbeModal" && !hasRegisteredWithEmail) {
          // stop job from opening in new tab
          event.preventDefault();
          toggleModalState(ModalState["email,redirectToJob"]);
          setRequestedJob(job.url);
          return;
        }
        trackCustomEvent({
          category: "SERP",
          action: `Job click`,
          label: window.location.pathname,
        });
      }}
    />
  );
}
